import { Fragment, useMemo, useState, useEffect, useRef } from "react";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import PageHeader from "../../Components/common/PageHeader";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { LogisticsCountry } from "../../Components/logistics/LogisticsCountry";
import { CityList } from "../../Components/logistics/CityList";
import { AddCitiesPanel } from "../../Components/logistics/AddCitiesPanel";
import { CityEditModal } from "../../Components/logistics/CityEditModal";

export const ConfigLogistics = () => {
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [countryShippingValue, setCountryShippingValue] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filterValue, setFilterValue] = useState("");

  const addPanelRef = useRef(null);

  const { getData, patchData, invalidateQuery } = useDataFetch();

  const { data: countriesData, isLoading } = getData(["countries-available"], "/product/countries-available/all");
  const countries = countriesData?.data?.countries ?? [];


  const { data, isLoading: isLoadingCities, refetch: refetchCities } = getData(
    ["citiesWithShippingCost", selectedCountryId, pagination],
    "/logistics/cities-with-shipping-cost",
    { enabled: false },
    {
      countryId: selectedCountryId || undefined,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      name: filterValue || undefined,
    }
  );


  const handleSearch = () => {
    if (!selectedCountryId) return;
    refetchCities({
      countryId: selectedCountryId,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      name: filterValue || undefined,
    });
  };


  const selectedCountry = useMemo(
    () => countries.find((c) => String(c.country_id) === String(selectedCountryId)) ?? null,
    [countries, selectedCountryId]
  );

  // El primer país se elige solo: la pantalla sin país no muestra nada y el
  // selector de antes obligaba a un clic extra siempre.
  useEffect(() => {
    if (!selectedCountryId && countries.length > 0) {
      setSelectedCountryId(String(countries[0].country_id));
    }
  }, [countries, selectedCountryId]);

  useEffect(() => {
    setCountryShippingValue(selectedCountry?.shippingCost ?? "");
    if (selectedCountryId) {
      refetchCities();
    }
  }, [selectedCountry, selectedCountryId, pagination]);

  const { mutateAsync: updateCountryShipping, isPending: updatingCountry } = patchData(
    selectedCountry ? `/logistics/countries/${selectedCountry.id}` : null,
    {},
    {
      onSuccess: () => {
        toast.success("Costo de envío del país actualizado correctamente");
        setEditMode(false);
        invalidateQuery(["countries-available"]);
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al actualizar costo del país");
      },
    }
  );

  const handleSaveShippingCost = () => {
    if (!selectedCountry) return;
    updateCountryShipping({ shippingCost: Number(countryShippingValue) });
  };


  const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen);


  const { mutateAsync: updateCitiesShipping } = patchData(
    "/logistics/cities",
    {},
    {
      onSuccess: (response) => {
        if (response?.message === "logistics_multiple_cities_shipping_cost_updated") {
          toast.success(`Se actualizaron ${response.data.modifiedCount} ciudades correctamente`);
          handleSearch();
          // Las ciudades guardadas dejan de estar "sin costo": la lista del
          // panel tiene que volver a pedirse o seguirían apareciendo.
          invalidateQuery(["cities-without-shipping"]);
        } else {
          toast.error("No se pudieron actualizar las ciudades");
        }
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al guardar las ciudades");
      },
    }
  );

  const handleSaveCities = (cityIds, shippingCost) =>
    updateCitiesShipping({ cityIds, shippingCost });


  const { mutateAsync: editCityShipping } = patchData(
    editingCity ? `/logistics/cities/${editingCity._id}` : null,
    {},
    {
      onSuccess: (response) => {
        if (response?.message === "logistics_city_shipping_cost_updated") {
          toast.success("Ciudad actualizada correctamente");
          handleSearch();
          toggleEditModal();
        } else {
          toast.error("No se pudo actualizar la ciudad");
        }
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al actualizar la ciudad");
      },
    }
  );

  const handleEditCity = (city) => {
    setEditingCity(city);
    setIsEditModalOpen(true);
  };

  return (
    <Fragment>
      <PageHeader
        title="Países y logística"
        crumbs={[{ label: "Configuración" }, { label: "Logística" }]}
      />

      {isLoading ? (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner size="sm" /> Cargando países...
        </div>
      ) : countries.length === 0 ? (
        <div className="ui-empty">No hay países disponibles todavía.</div>
      ) : (
        <>
          <div className="ui-filterbar">
            {countries.map((country) => (
              <button
                key={country.country_id}
                type="button"
                className={`ui-pill${String(country.country_id) === String(selectedCountryId) ? " ui-pill--active" : ""}`}
                onClick={() => {
                  setSelectedCountryId(String(country.country_id));
                  setEditMode(false);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
              >
                {country.name}
              </button>
            ))}
          </div>

          {selectedCountry && (
            <>
              <div className="mb-3">
                <LogisticsCountry
                  selectedCountry={selectedCountry}
                  countryShippingValue={countryShippingValue}
                  setCountryShippingValue={setCountryShippingValue}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  handleSaveShippingCost={handleSaveShippingCost}
                  isPending={updatingCountry}
                  cityCount={data?.data?.total}
                  onAddCities={() =>
                    addPanelRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    })
                  }
                />
              </div>

              <div className="ui-grid-narrow">
                <CityList
                  onEdit={handleEditCity}
                  data={data}
                  isLoading={isLoadingCities}
                  pagination={pagination}
                  setPagination={setPagination}
                  filterValue={filterValue}
                  onFilterChange={setFilterValue}
                  onSearch={handleSearch}
                  currency={selectedCountry.currency}
                />

                <AddCitiesPanel
                  ref={addPanelRef}
                  countryId={selectedCountryId}
                  onSave={handleSaveCities}
                  currency={selectedCountry.currency}
                />
              </div>
            </>
          )}
        </>
      )}

      {isEditModalOpen && editingCity && (
        <CityEditModal
          city={editingCity}
          isModalOpen={isEditModalOpen}
          toggleModal={toggleEditModal}
          handleSaveEdit={(shippingCost) => editCityShipping({ shippingCost })}
          currency={selectedCountry?.currency}
        />
      )}
    </Fragment>
  );
};
